import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAccount } from "@/contexts/account.context";
import { useAuth } from "@/contexts/auth.context";
import { useIntegrationContext } from "@/contexts/IntegrationContext";
import { authDbg } from "@/lib/auth-debug-logger";
import { ehPlanoDeAgencia } from "@/lib/account-access-context";
import { getUserConnectionsApiV1UsersMeConnectionsGet } from "@/backend-sdk/client/sdk.gen";
import { tenantAtivo } from "@/lib/hey-api-client";
import { Skeleton } from "@/components/ui/skeleton";

interface IntegrationGuardProps {
  children: React.ReactNode;
  fallbackRoute?: string;
}

const IntegrationGuard = ({
  children,
  fallbackRoute,
}: IntegrationGuardProps) => {
  const { user } = useAuth();
  const { isReadOnly, acessoResolvido, selectedAccount } = useAccount();
  const { state } = useIntegrationContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasIntegrations, setHasIntegrations] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkIntegrations = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      // Se o usuário não é subscriber, permitir acesso
      if (user.user_type !== "subscriber" && user.user_type !== "SUBSCRIBER") {
        setHasIntegrations(true);
        setLoading(false);
        return;
      }

      // Quem só lê a loja passa direto, e este ramo **não** é conveniência: sem ele o app
      // entra em laço. O funcionário nasce com `user_type = SUBSCRIBER` (é o que
      // `POST /my-account/users` grava, como toda criação de usuário do produto), então o
      // ramo acima não o pega; e se a loja em que ele está ainda não tiver conexão ativa,
      // este guard o mandaria para `/dashboard/integrations` — que, para ele, redireciona
      // de volta para `/dashboard`, que passa por este guard de novo.
      //
      // Mandá-lo para lá também não faria sentido nenhum: conectar provedor é escrita, e a
      // API a recusa a quem é `viewer` na loja.
      //
      // <M-248> A pergunta passou a ser sobre o nível, e não sobre a trilha: o `editor`
      // segue adiante e cai na tela de integrações como qualquer dono de loja, porque para
      // ele ela funciona. Enquanto o contexto de contas não sabe qual é a loja ativa,
      // `isReadOnly` é `true` e este ramo responde — o que evita justamente o laço, já que
      // a rota de destino ainda desviaria.
      if (isReadOnly) {
        setHasIntegrations(true);
        setLoading(false);
        return;
      }

      try {
        // Buscar conexões unificadas do usuário
        const { data: connectionsResponse } =
          await getUserConnectionsApiV1UsersMeConnectionsGet({
            throwOnError: true,
            // <D-062> O estado das conexões é da **loja**. Sem o `customer_uuid` a rota cai
            // na conta ativa do token, que um token de login comum não traz.
            query: { customer_uuid: tenantAtivo() },
          });
        const connections = connectionsResponse.connections || [];

        // Verificar se há pelo menos uma conexão ativa
        const hasConnected = connections.some(
          (connection) => connection.connected === true,
        );

        authDbg(
          `[AUTH_DBG] IntegrationGuard: conexões encontradas: ${connections.length}`,
        );
        authDbg(
          `[AUTH_DBG] IntegrationGuard: conexões ativas: ${hasConnected}`,
        );
        authDbg(
          `[AUTH_DBG] IntegrationGuard: subaccount token present: ${!!localStorage.getItem("subaccount_access_token")}`,
        );

        setHasIntegrations(hasConnected);
      } catch (error) {
        authDbg(
          `[AUTH_DBG] IntegrationGuard: erro ao verificar integrações: ${error}`,
        );
        // Em caso de erro, permitir acesso para não quebrar a navegação
        setHasIntegrations(true);
      } finally {
        setLoading(false);
      }
    };

    // <M-248> Enquanto a loja ativa não foi escolhida, `isReadOnly` é o palpite fechado do
    // primeiro instante e não descreve loja nenhuma. `loading` não servia para esperar: ele
    // vai a `false` na montagem do app, com `user` ainda nulo, e nunca volta. Achado de
    // code review.
    if (!acessoResolvido) return;
    checkIntegrations();
    // A loja ativa entra nas dependências porque a resposta é dela: o `editor` que troca de
    // uma loja conectada para outra sem conexão continuaria com o `hasIntegrations` da
    // anterior, e o desvio para `/dashboard/integrations` nunca aconteceria. Achado de code
    // review.
  }, [user, isReadOnly, acessoResolvido, selectedAccount?.id]);

  useEffect(() => {
    if (hasIntegrations === false && user && !loading) {
      // Se não tem integrações, verificar tipo de plano e redirecionar
      const planCategory = user.subscription?.plan_category || "lojista";

      // <M-214> A grafia da categoria mora num lugar só. O lojista com várias contas
      // continua caindo nas integrações: ele sempre tem uma conta ativa por padrão, e o
      // modal de seleção fica por cima de qualquer rota do dashboard, então a escolha
      // continua sendo pedida sem que a tela de configuração se perca.
      if (ehPlanoDeAgencia(planCategory)) {
        navigate("/dashboard/accounts", { replace: true });
      } else {
        navigate("/dashboard/integrations", { replace: true });
      }
    }
  }, [hasIntegrations, user, loading, navigate, location.pathname]);

  // Mostrar loading enquanto verifica
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="space-y-3">
          <Skeleton className="h-12 w-12 rounded-full mx-auto" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
    );
  }

  // Se tem integrações, permitir acesso
  if (hasIntegrations === true) {
    return <>{children}</>;
  }

  // Se não tem integrações, mostrar loading até redirecionar
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="space-y-3">
        <Skeleton className="h-12 w-12 rounded-full mx-auto" />
        <Skeleton className="h-4 w-32" />
      </div>
    </div>
  );
};

export default IntegrationGuard;
